import * as XLSX from "xlsx";
import { normalizeRevenuePayload, buildRevenueTeardownData, DIM_LABELS } from "./teardownAdapter";

/** "$10.6B" | "$775M" | number -> absolute number */
function moneyToNumber(v) {
  if (typeof v === "number") return v;
  const s = String(v).replace(/[\$,]/g, "").trim().toUpperCase();
  if (!s) return 0;
  if (s.endsWith("B")) return parseFloat(s) * 1e9;
  if (s.endsWith("M")) return parseFloat(s) * 1e6;
  return parseFloat(s);
}

/** Pretty USD formatter (compact: K/M/B) for display cells */
const fmtUSD = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});
function formatUSD(n) {
  const x = Number(n) || 0;
  return fmtUSD.format(x);
}

function pct(val) {
  if (val === null || val === undefined || isNaN(val)) return "";
  const n = Math.abs(val) <= 1 ? val * 100 : val; // 0.152 or 15.2 -> %
  return Number(n).toFixed(1) + "%";
}

function addSheetFromList(wb, title, dimLabel, list = []) {
  const rows = list.map((r, i) => {
    const n = moneyToNumber(r.value ?? 0);
    return {
      Rank: i + 1,
      [dimLabel]: r.label ?? "",
      "Revenue ($)": n,
      "Revenue (Display)": formatUSD(n),
      "Share %": pct(r.sharePct),
      "Growth %": pct(r.growthPct),
    };
  });
  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = [
    { wch: 6 },
    { wch: Math.max(18, dimLabel.length + 4) },
    { wch: 16 },
    { wch: 18 },
    { wch: 10 },
    { wch: 10 },
  ];
  XLSX.utils.book_append_sheet(wb, ws, title.slice(0, 31));
}

/** Flattens parent->child breakdowns into one long table (with full labels) */
function addBreakdownsSheet(wb, root, dimLabels) {
  const out = [];
  const dims = Object.keys(root || {});
  dims.forEach((dKey) => {
    const dLabel = dimLabels[dKey] || dKey;
    (root[dKey] || []).forEach((parent) => {
      const childKeys = Object.keys(parent.breakdowns || {});
      childKeys.forEach((ck) => {
        const ckLabel = dimLabels[ck] || ck;
        (parent.breakdowns[ck] || []).forEach((child) => {
          const n = moneyToNumber(child.value ?? 0);
          out.push({
            "Parent Dimension": dLabel,
            Parent: parent.label,
            "Child Dimension": ckLabel,
            Child: child.label,
            "Revenue ($)": n,
            "Revenue (Display)": formatUSD(n),
            "Share %": pct(child.sharePct),
            "Growth %": pct(child.growthPct),
          });
        });
      });
    });
  });
  if (!out.length) return;
  const ws = XLSX.utils.json_to_sheet(out);
  ws["!cols"] = [
    { wch: 18 },
    { wch: 28 },
    { wch: 18 },
    { wch: 28 },
    { wch: 16 },
    { wch: 20 },
    { wch: 10 },
    { wch: 10 },
  ];
  XLSX.utils.book_append_sheet(wb, ws, "Breakdowns");
}

/** NEW: Sources sheet */
function addSourcesSheet(wb, sources = []) {
  if (!sources?.length) return;
  const rows = sources.map((s) => ({
    "Source ID": s.source_id ?? "",
    Type: s.type ?? "",
    Title: s.title ?? "",
    Date: s.date ?? "",
    Reliability: s.reliability ?? "",
    URL: s.url ?? "",
    Relevance: s.relevance ?? "",
    "Segments Supported": Array.isArray(s.segments_supported)
      ? s.segments_supported.join(", ")
      : (s.segments_supported ?? ""),
    "Key Data Extracted": Array.isArray(s.key_data_extracted)
      ? s.key_data_extracted.join(" | ")
      : (s.key_data_extracted ?? ""),
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = [
    { wch: 12 },
    { wch: 14 },
    { wch: 60 },
    { wch: 12 },
    { wch: 14 },
    { wch: 44 },
    { wch: 18 },
    { wch: 28 },
    { wch: 50 },
  ];
  XLSX.utils.book_append_sheet(wb, ws, "Sources");
}

/** NEW: Base Facts sheet */
function addBaseFactsSheet(wb, baseFacts = []) {
  if (!baseFacts?.length) return;
  const rows = baseFacts.map((f) => ({
    "Fact ID": f.fact_id ?? "",
    Category: f.category ?? "",
    Statement: f.statement ?? "",
    "Value (raw)": f.value ?? "",
    "Value ($)": typeof f.value === "number" ? f.value : "",
    "Source ID": f.source_id ?? "",
    Reliability: f.reliability ?? "",
    "Page Ref": f.page_reference ?? "",
    Relevance: f.relevance ?? "",
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = [
    { wch: 10 },
    { wch: 18 },
    { wch: 80 },
    { wch: 14 },
    { wch: 16 },
    { wch: 12 },
    { wch: 14 },
    { wch: 14 },
    { wch: 18 },
  ];
  XLSX.utils.book_append_sheet(wb, ws, "Base Facts");
}

/** Optional: Assumptions sheet (handy if present) */
function addAssumptionsSheet(wb, assumptions = []) {
  if (!assumptions?.length) return;
  const rows = assumptions.map((a) => ({
    "Assumption ID": a.assumption_id ?? "",
    Category: a.category ?? "",
    Statement: a.statement ?? "",
    Confidence: a.confidence ?? "",
    Sensitivity: a.sensitivity ?? "",
    Rationale: a.rationale ?? "",
    "Affects Segments": Array.isArray(a.affects_segments) ? a.affects_segments.join(", ") : (a.affects_segments ?? ""),
    "Supporting Sources": Array.isArray(a.supporting_sources) ? a.supporting_sources.join(", ") : (a.supporting_sources ?? ""),
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = [
    { wch: 14 },
    { wch: 18 },
    { wch: 80 },
    { wch: 14 },
    { wch: 14 },
    { wch: 80 },
    { wch: 30 },
    { wch: 30 },
  ];
  XLSX.utils.book_append_sheet(wb, ws, "Assumptions");
}

/** Main entry: consumes your built `data` and downloads an .xlsx */
export function exportRevenueExcel(data, fileName = "Revenue_Export.xlsx") {
  if (!data) return;

  const wb = XLSX.utils.book_new();

  // Dimension labels (use full names from your data)
  const dimLabels = Object.fromEntries((data?.dimensions || []).map((d) => [d.key, d.label]));

  // Optional cover sheet with currency
  const totalNum = moneyToNumber(data?.context?.totalRevenue ?? 0);
  const cover = [
    {
      Company: data?.company?.name || "Company",
      "Total Revenue ($)": totalNum,
      "Total Revenue (Display)": formatUSD(totalNum),
      "Avg Growth %": data?.context?.avgGrowthPct ?? "",
      FY: data?.context?.fiscalYear || "",
    },
  ];
  const wsCover = XLSX.utils.json_to_sheet(cover);
  wsCover["!cols"] = [
    { wch: 30 },
    { wch: 20 },
    { wch: 24 },
    { wch: 14 },
    { wch: 10 },
  ];
  XLSX.utils.book_append_sheet(wb, wsCover, "Summary");

  // Sheets by dimension (full names, not keys like "geo")
  const plan = [
    { key: "business",    title: "By Business Segment", label: dimLabels.business || "Business Segment" },
    { key: "product",     title: "By Product Category", label: dimLabels.product  || "Product Category" },
    { key: "application", title: "By Application Market", label: dimLabels.application || "Application Market" },
    { key: "geo",         title: "By Geography",        label: dimLabels.geo      || "Geography" },
  ];
  plan.forEach(({ key, title, label }) => {
    const list = data?.root?.[key] || [];
    if (list.length) addSheetFromList(wb, title, label, list);
  });

  // Flattened breakdowns with full dimension names + $ formatting
  addBreakdownsSheet(wb, data?.root, dimLabels);

  // ⬇⬇ NEW: append sources / base facts / assumptions if present on `data`
  addSourcesSheet(wb, data?.sources || data?.extras?.sources || []);
  addBaseFactsSheet(wb, data?.baseFacts || data?.extras?.baseFacts || []);
  addAssumptionsSheet(wb, data?.assumptions || data?.extras?.assumptions || []);

  XLSX.writeFile(wb, fileName);
}

/**
 * Convenience: export directly from raw payload (Prompt-5 combined or legacy).
 * It preserves Sources / Base Facts even if your caller doesn’t pass them separately.
 */
export function exportRevenueExcelFromPayload(payload, fileName = "Revenue_Export.xlsx") {
  const { rows, meta, sources = [], baseFacts = [], assumptions = [] } = normalizeRevenuePayload(payload);
  if (!rows?.length) return;

  const data = buildRevenueTeardownData(
    rows,
    ["geo", "business", "product", "application", "customer", "channel"],
    "revenue_millions",
    undefined,
    {
      company: {
        name: meta?.company_name,
        annualRevenueOverride: meta?.annual_revenue_millions, // millions
      },
      context: { fiscalYear: meta?.fiscal_Year },
      defaultAvgGrowthPct: 15,
      rowGrowthFields: ["growthPct", "yoy_pct", "yoy"],

      // Make extras visible to exporter even if build(...) omits them internally
      sources,
      baseFacts,
      assumptions,
    }
  );

  // Ensure sheets get them regardless of build(...) internals
  data.extras = { sources, baseFacts, assumptions };

  exportRevenueExcel(data, fileName);
}
